// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';

// polyfills
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

let chartData;
let rawData;

const margins = {
  top: 20,
  right: 80,
  bottom: 25,
  left: 35,
};

let minValue;
let maxValue;

let minDomain;
let maxDomain;

let lastData;

let dataNote;
let summary;
let hed;
let seriesTitle;

window.onload = () => {
  // Initialize pymChild if not already defined
  if (!window.pymChild) {
    window.pymChild = new Child({ id: pkg.name });
  }

  // Safely call sendHeight if pymChild exists
  if (window.pymChild) {
    window.pymChild.sendHeight();
  }

  // Get the dropdown menu
  const dropdown = document.getElementById('commodity');

  // Function to get URL parameter
  const getUrlParameter = (name) => {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
  };

  // Map short commodity names to the full filenames
  const commodityMap = {
    bacon: 'bacon_latest.json',
    bread: 'bread_latest.json',
    cheddar_cheese: 'cheddar_cheese_latest.json',
    chicken: 'chicken_latest.json',
    chicken_legs: 'chicken_legs_latest.json',
    coffee: 'coffee_latest.json',
    cookies: 'cookies_latest.json',
    eggs: 'eggs_latest.json',
    flour: 'flour_latest.json',
    gasoline: 'gasoline_latest.json',
    ground_beef: 'ground_beef_latest.json',
    ice_cream: 'ice_cream_latest.json',
    milk: 'milk_latest.json',
    potato_chips: 'potato_chips_latest.json',
    rice: 'rice_latest.json',
    spaghetti: 'spaghetti_and_macaroni_latest.json',
    steak: 'steak_latest.json',
    strawberries: 'strawberries_latest.json',
    sugar: 'sugar_latest.json',
    tomatoes: 'tomatoes_latest.json',
  };

  // Get the commodity from the URL or default to the dropdown's value
  const commodityParam = getUrlParameter('commodity');
  const defaultCommodity = commodityParam && commodityMap[commodityParam]
    ? commodityMap[commodityParam]
    : dropdown.value; // Fallback to dropdown default if param is missing or invalid

  // Set the dropdown to match the selected commodity
  dropdown.value = defaultCommodity;

  // Load data for the selected commodity
  loadData(defaultCommodity);

  // Add event listener to handle changes in the dropdown
  dropdown.addEventListener('change', (event) => {
    const selectedFile = event.target.value;
    loadData(selectedFile);
  });

  // Ensure the chart redraws on window resize
  window.onresize = () => {
    draw();
    if (window.pymChild) {
      window.pymChild.sendHeight();
    }
  };
};


// Function to capitalize text for the headline
function toSentenceCase(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// Parse the date format used in the JSON
const pTime = d3.timeParse('%Y-%m-%d');

// Fetch data for the selected commodity and update the chart
function loadData(fileName) {
  const baseUrl = 'https://ix.cnn.io/data/inflation/';
  d3.json(`${baseUrl}${fileName}`).then((jsonData) => {
    const { data } = jsonData;
    chartData = data.map(d => ({
      date: d3.timeParse('%Y-%m-%d')(d.date),
      yr: +d.date.split('-')[0],
      value: d.price,
    })).filter(d => d.date >= new Date('2016-01-01'));

    if (!chartData || chartData.length === 0) {
      console.warn('No data available for this commodity.');
      draw();
      return;
    }

    minDomain = d3.min(chartData, d => d.date);
    maxDomain = d3.max(chartData, d => d.date);

    minValue = 0;
    maxValue = d3.max(chartData, d => d.value + 1.5);

    lastData = chartData.filter(d => d.date == maxDomain)[0];

    if (jsonData.lastUpdatedStr && jsonData.latestMonthStr) {
      dataNote = `Note: Price values aren't adjusted for seasonal swings. Data current through ${jsonData.latestMonthStr} as of ${jsonData.lastUpdatedStr}. Because of a government shutdown, the bureau didn't release figures for October 2025. The "previous month" in November 2025 refers to September 2025.`;
    }

    if (jsonData.priceSummaryHtml) {
      summary = jsonData.priceSummaryHtml;
    }

    if (jsonData.commidityName) {
      hed = jsonData.commidityName;
    }

    if (jsonData.seriesTitle) {
      seriesTitle = jsonData.seriesTitle;
    }

    draw();
  }).catch((error) => {
    console.error('Error loading data:', error);
    chartData = []; // Set to empty array to avoid rendering issues
    draw(); // Redraw to display error message
  });
}


// Draw the chart
function draw() {
  const container = d3.select('#graphic');
  container.html(''); // Clear the existing chart

  // Check if chartData is valid and not empty
  if (!chartData || chartData.length === 0) {
    container.append('p')
      .text('No data available to display.')
      .attr('class', 'no-data-message');
    window.pymChild.sendHeight();
    return; // Exit the draw function early
  }

  // Update the headline and summary
  if (summary) {
    d3.select('.dek').html(summary);
  }

  if (seriesTitle) {
    d3.select('.chart-line').html(seriesTitle);
  }

  if (hed) {
    d3.select('.headline').html(`Tracking US inflation: The average price of ${hed}`);
  }

  // Calculate dimensions for the chart
  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${margins.left},${margins.top})`);

  // Add the highlight rectangle for the COVID-19 emergency period
  const highlightStart = new Date('2020-01-31');
  const highlightEnd = new Date('2023-05-11');

  g.append('rect')
    .attr('x', xScale(highlightStart))
    .attr('y', 0)
    .attr('width', xScale(highlightEnd) - xScale(highlightStart))
    .attr('height', chartHeight)
    .attr('fill', '#e9e9e9')
    .attr('opacity', 0.4);

  // X-Axis
  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(0,${chartHeight})`)
    .call(d3.axisBottom(xScale).ticks(isMobile ? 3 : 5).tickFormat(d3.timeFormat('%b ’%y')));

  // Y-Axis
  const yAxis = g.append('g')
    .attr('class', 'grid y')
    .call(
      d3.axisLeft(yScale)
        .ticks(5)
        .tickSize(-chartWidth)
        .tickFormat(d => (d === 0 ? '$0' : d < 1 ? `$${d3.format('.2f')(d)}` : `$${d3.format('.2f')(d).replace('.00', '')}`)),
    );

  // Remove the Y-axis domain line
  yAxis.select('.domain').remove();

  // Avoid drawing a gridline for the top-most value
  yAxis.selectAll('.tick line')
    .filter(d => d === maxValue)
    .remove();

  // Line
  const path = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y(d => yScale(d.value));

  const area = d3.area()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y0(yScale(0))
    .y1(d => yScale(d.value));

  g.append('path')
    .datum(chartData)
    .attr('class', 'area')
    .attr('fill', '#7c4ea5')
    .attr('opacity', 0.12)
    .attr('d', area);

  g.append('path')
    .datum(chartData)
    .attr('fill', 'none')
    .attr('stroke', '#7c4ea5')
    .attr('stroke-width', 1.5)
    .attr('d', path);

  // Annotate the last data point
  const annotation = g.append('g')
    .attr('class', 'annotation')
    .attr('transform', `translate(${xScale(lastData.date)},${yScale(lastData.value)})`);

  annotation.append('circle').attr('class', 'pulse-ring').attr('r', 4).attr('fill', '#7c4ea5');
  annotation.append('circle').attr('class', 'end-dot').attr('r', 4).attr('fill', '#7c4ea5');
  annotation.append('text').attr('dx', 10).attr('class', 'annotation-date').text(d3.timeFormat('%b %Y')(lastData.date));
  annotation.append('text').attr('dx', 10).attr('dy', '1.2em').attr('class', 'annotation-value')
    .text(`$${d3.format('.2f')(lastData.value)}`);

  // Add text label for the COVID-19 period
  g.append('text')
    .attr('x', xScale(highlightStart) + (xScale(highlightEnd) - xScale(highlightStart)) / 2)
    .attr('y', 20)
    .attr('text-anchor', 'middle')
    .attr('class', 'highlight-label')
    .text('COVID-19 emergency')
    .style('font-size', '12px');

  // Update the data note
  if (dataNote) {
    d3.select('.datanote').html(dataNote);
  }

  // Notify the parent iframe of height changes
  window.pymChild.sendHeight();
}
